import { compose } from "redux";
import { connect } from "react-redux";
import { Grid } from "@material-ui/core";
import Delete from "@material-ui/icons/Delete";
import React, { useEffect, useMemo } from "react";
import { formValueSelector } from "redux-form/immutable";

import {
  ACTIONS,
  FIELD_FORM,
  SORT_DIRECTION,
} from "features/planner/planner.constants";

import {
  editFieldOnSubmit,
  initializeEditField,
  createFieldOnSubmit,
  settingsFieldsOnLoad,
  deleteField,
} from "features/planner/planner.actions";

import {
  getSummarizedDataStatesFromState,
  getCurrentUserAppRolesFromState,
} from "app/app.selectors";

import CrudBasePage from "app/components/CrudBasePage";
import { getAccessControlButton } from "utils/component.util";
import { fieldStatusToString } from "features/planner/planner.mappers";
import { getFieldsFromState } from "features/planner/planner.selectors";
import FieldForm from "features/planner/settings/fields/components/FieldForm";
import { hasRequiredAccessLevel, getModifiedByValue } from "utils/app.util";

const editFieldSelector = formValueSelector(FIELD_FORM.ID);

const SettingsFieldsContainer = ({
  Icon,
  fields,
  sourceUri,
  dataState,
  breadcrumb,
  dispatchOnLoad,
  crudAccessLevel,
  dispatchDeleteField,
  currentUserAppRoles,
  dispatchEditFieldOnSubmit,
  dispatchInitializeEditField,
  dispatchCreateFieldOnSubmit,
}) => {
  useEffect(() => {
    dispatchOnLoad();
  }, [dispatchOnLoad]);

  const columns = useMemo(
    () => [
      {
        xs: true,
        title: "Name",
        getSortProperty: item => item.get("name"),
      },
      {
        xs: true,
        title: "Operator",
        getSortProperty: item => item.get("operator"),
      },
      {
        xs: true,
        title: "Status",
        getSortProperty: item => fieldStatusToString(item.get("status")),
      },
      {
        xs: true,
        title: "Country",
        getSortProperty: item => item.getIn(["country", "name"]),
      },
      {
        xs: true,
        component: Grid,
        title: "Modified by",
        getSortProperty: item => item.get("modifiedBy"),
        getValue: getModifiedByValue,
      },
    ],
    [],
  );

  const actions = useMemo(
    () => [
      getAccessControlButton(
        crudAccessLevel.delete,
        dispatchDeleteField,
        <Delete fontSize="small" />,
        "Delete",
      ),
    ],
    [crudAccessLevel, dispatchDeleteField],
  );

  return (
    <CrudBasePage
      Icon={Icon}
      title={breadcrumb}
      items={fields}
      displayAddButton={hasRequiredAccessLevel(
        crudAccessLevel.create,
        currentUserAppRoles,
      )}
      displaySearchField
      dataState={dataState}
      EditEntityFormComponent={FieldForm}
      CreateEntityFormComponent={FieldForm}
      SortableListProps={{
        defaultSortColumn: 0,
        actions: actions,
        columns: columns,
        getKey: crew => crew.get("id"),
        defaultSortDirection: SORT_DIRECTION.ASC,
        onRowClick: hasRequiredAccessLevel(
          crudAccessLevel.edit,
          currentUserAppRoles,
        )
          ? dispatchInitializeEditField
          : null,
      }}
      EditModalProps={{
        submitText: "Save",
        title: "Edit field",
        displaySubmitBtn: !sourceUri,
        formTitleField: FIELD_FORM.NAME,
        onSubmit: dispatchEditFieldOnSubmit,
      }}
      CreateModalProps={{
        submitText: "Create",
        title: "Add field",
        onSubmit: dispatchCreateFieldOnSubmit,
      }}
      editEntityForm={{
        form: FIELD_FORM.ID,
      }}
      createEntityForm={{
        form: FIELD_FORM.ID,
      }}
    />
  );
};

export default compose(
  connect(
    state => ({
      fields: getFieldsFromState(state),
      currentUserAppRoles: getCurrentUserAppRolesFromState(state),
      sourceUri: editFieldSelector(state, FIELD_FORM.SOURCE_URI),
      dataState: getSummarizedDataStatesFromState(
        state,
        ACTIONS.GET_FIELDS,
        ACTIONS.DELETE_FIELD,
      ),
    }),
    {
      dispatchOnLoad: settingsFieldsOnLoad,
      dispatchEditFieldOnSubmit: editFieldOnSubmit,
      dispatchDeleteField: deleteField,
      dispatchCreateFieldOnSubmit: createFieldOnSubmit,
      dispatchInitializeEditField: initializeEditField,
    },
  ),
)(SettingsFieldsContainer);
